import { useCallback, useEffect, useMemo, useState } from "react";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import AdminPageShell from "../../components/admin/AdminPageShell";
import {
  adminInputInlineClass,
  adminPrimaryButtonClass,
  adminSecondaryButtonClass,
  adminSpinnerClass,
} from "../../components/admin/adminStyles";
import axiosInstance from "../../hooks/axios";
import {
  applyUpliftsFromBase,
  buildCalculatorRows,
  calculateMileageFare,
  createNextBand,
  MileageBand,
  MileageVehicle,
  sortMileageVehicles,
  syncVehicleRatesToBands,
} from "../../utils/mileagePricing";
import { vehicleTypeLabel } from "../../utils/vehicleTypeLabel";
import { formatMoney } from "../../utils/formatMoney";

const PRESET_DISTANCES = [3, 7, 12, 25, 60, 100];

export default function PerMilePrices() {
  const [vehicles, setVehicles] = useState<MileageVehicle[]>([]);
  const [bands, setBands] = useState<MileageBand[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [customMiles, setCustomMiles] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/mileage-pricing");
      setVehicles(res.data.vehicles ?? []);
      setBands(res.data.bands ?? []);
    } catch {
      setMessage("Failed to load mileage pricing");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const orderedVehicles = useMemo(
    () => sortMileageVehicles(vehicles),
    [vehicles]
  );

  const calculatorRows = useMemo(
    () => buildCalculatorRows(orderedVehicles, PRESET_DISTANCES),
    [orderedVehicles]
  );

  const customDistance = parseFloat(customMiles);
  const customFares = useMemo(() => {
    if (!Number.isFinite(customDistance) || customDistance <= 0) return null;
    return orderedVehicles.map((v) => ({
      vehicleTypeId: v.vehicleTypeId,
      description: v.description,
      fare: calculateMileageFare(customDistance, v.rates, v.minimumFare),
    }));
  }, [orderedVehicles, customDistance]);

  const updateRate = (vehicleId: string, serialNo: number, value: string) => {
    const num = value === "" ? null : Number(value);
    setVehicles((prev) =>
      prev.map((v) =>
        v.vehicleTypeId === vehicleId
          ? {
              ...v,
              rates: v.rates.map((r) =>
                r.serialNo === serialNo ? { ...r, ratePerMile: num } : r
              ),
            }
          : v
      )
    );
  };

  const updateMinFare = (vehicleId: string, value: string) => {
    const num = value === "" ? null : Number(value);
    setVehicles((prev) =>
      prev.map((v) =>
        v.vehicleTypeId === vehicleId ? { ...v, minimumFare: num } : v
      )
    );
  };

  const updateUplift = (
    vehicleId: string,
    field: "upliftMode" | "upliftAmount",
    value: string | "percent" | "flat"
  ) => {
    setVehicles((prev) =>
      prev.map((v) => {
        if (v.vehicleTypeId !== vehicleId) return v;
        if (field === "upliftMode") {
          return { ...v, upliftMode: value as "percent" | "flat" };
        }
        return { ...v, upliftAmount: value === "" ? null : Number(value) };
      })
    );
  };

  const setBaseVehicle = (vehicleId: string) => {
    setVehicles((prev) =>
      prev.map((v) => ({
        ...v,
        isMileageBase: v.vehicleTypeId === vehicleId,
        upliftMode: v.vehicleTypeId === vehicleId ? null : v.upliftMode ?? "percent",
      }))
    );
  };

  const handleApplyUplifts = () => {
    setVehicles(applyUpliftsFromBase(vehicles));
    setMessage("Uplift rates applied from base vehicle");
  };

  const handleAddBand = () => {
    const nextBand = createNextBand(bands);
    const nextBands = [...bands, nextBand];
    setBands(nextBands);
    setVehicles((prev) => syncVehicleRatesToBands(prev, nextBands));
  };

  const handleRemoveBand = (serialNo: number) => {
    const remaining = bands.filter((band) => band.serialNo !== serialNo);
    const nextBands = remaining.map((band, index) => ({
      ...band,
      fromMiles:
        index === 0
          ? 0
          : Math.round((remaining[index - 1].toMiles + 0.01) * 100) / 100,
    }));
    setBands(nextBands);
    setVehicles((prev) => syncVehicleRatesToBands(prev, nextBands));
  };

  const handleBandChange = (
    serialNo: number,
    field: "fromMiles" | "toMiles",
    value: string
  ) => {
    const num = value === "" ? 0 : Number(value);
    const nextBands = bands.map((band) =>
      band.serialNo === serialNo ? { ...band, [field]: num } : band
    );
    setBands(nextBands);
    setVehicles((prev) => syncVehicleRatesToBands(prev, nextBands));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage("");
    try {
      const res = await axiosInstance.put("/mileage-pricing", {
        bands,
        vehicles: orderedVehicles.map((v, i) => ({
          vehicleTypeId: v.vehicleTypeId,
          isMileageBase: v.isMileageBase,
          minimumFare: v.minimumFare,
          upliftMode: v.isMileageBase ? null : v.upliftMode,
          upliftAmount: v.isMileageBase ? null : v.upliftAmount,
          displayOrder: i,
          rates: v.rates.map((r) => ({
            serialNo: r.serialNo,
            ratePerMile: r.ratePerMile,
          })),
        })),
      });
      setVehicles(res.data.vehicles);
      setBands(res.data.bands ?? []);
      setMessage("Mileage pricing saved successfully");
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Save failed";
      setMessage(msg);
    } finally {
      setSaving(false);
    }
  };

  const inputClass = `${adminInputInlineClass} min-w-[4rem] text-center`;

  if (loading) {
    return (
      <AdminPageShell className="max-w-[1400px]">
        <PageBreadcrumb
          pageTitle="Per Mile Prices"
          description="Mileage bands, uplift pricing, and fare calculator"
        />
        <div className="flex justify-center py-12">
          <div className={adminSpinnerClass} />
        </div>
      </AdminPageShell>
    );
  }

  return (
    <AdminPageShell className="max-w-[1400px]">
      <PageBreadcrumb
        pageTitle="Per Mile Prices"
        description="Mileage bands, uplift pricing, and fare calculator"
      />

      <div className="flex flex-wrap items-center justify-end gap-3">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className={adminPrimaryButtonClass}
        >
          {saving ? "Saving..." : "Save all pricing"}
        </button>
      </div>
      {message && (
        <p
          className={`mt-2 text-sm ${
            message.includes("Failed") || message.includes("failed")
              ? "text-red-600"
              : "text-green-600"
          }`}
        >
          {message}
        </p>
      )}

      {vehicles.length === 0 && (
        <p className="mt-6 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
          Add at least one active vehicle type before configuring per-mile pricing.
        </p>
      )}

      {/* Mileage rates */}
      <section className="mt-8 rounded-2xl border border-gray-200 bg-white shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] overflow-x-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 px-6 pt-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Mileage rates</h2>
            <p className="text-sm text-gray-500 mt-1">
              Add distance bands, then set the per-mile rate for each vehicle type. Mark one
              vehicle as the base; other types can use uplift pricing below.
            </p>
          </div>
          <button
            type="button"
            onClick={handleAddBand}
            disabled={vehicles.length === 0}
            className={`${adminSecondaryButtonClass} disabled:opacity-50`}
          >
            Add distance band
          </button>
        </div>
        {bands.length === 0 ? (
          <p className="px-6 py-8 text-sm text-gray-500">
            No distance bands yet. Click &quot;Add distance band&quot; to define mile ranges and
            per-mile rates.
          </p>
        ) : (
        <table className="min-w-full text-sm border-collapse mt-4">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th colSpan={3} className="px-3 py-2 text-left font-medium text-gray-600 border-r">
                Mile range
              </th>
              {orderedVehicles.map((v) => (
                <th
                  key={v.vehicleTypeId}
                  className="px-2 py-2 text-center font-medium text-gray-700 min-w-[100px]"
                >
                  <div>{vehicleTypeLabel(v)}</div>
                  <label className="mt-1 flex items-center justify-center gap-1 text-xs font-normal text-gray-500">
                    <input
                      type="radio"
                      name="mileageBase"
                      checked={v.isMileageBase}
                      onChange={() => setBaseVehicle(v.vehicleTypeId)}
                    />
                    Base
                  </label>
                </th>
              ))}
            </tr>
            <tr className="bg-gray-50 border-b text-xs text-gray-500">
              <th className="px-3 py-1 border-r">FROM</th>
              <th className="px-3 py-1 border-r">TO</th>
              <th className="px-3 py-1 border-r w-16" />
              {orderedVehicles.map((v) => (
                <th key={v.vehicleTypeId} className="px-2 py-1">
                  £/mile
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bands.map((band) => (
              <tr key={band.serialNo} className="border-b hover:bg-gray-50/50">
                <td className="px-2 py-1 border-r">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className={inputClass}
                    value={band.fromMiles}
                    onChange={(e) =>
                      handleBandChange(band.serialNo, "fromMiles", e.target.value)
                    }
                  />
                </td>
                <td className="px-2 py-1 border-r">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className={inputClass}
                    value={band.toMiles}
                    onChange={(e) =>
                      handleBandChange(band.serialNo, "toMiles", e.target.value)
                    }
                  />
                </td>
                <td className="px-2 py-1 border-r text-center">
                  <button
                    type="button"
                    onClick={() => handleRemoveBand(band.serialNo)}
                    className="text-xs text-red-600 hover:text-red-800"
                    aria-label="Remove band"
                  >
                    Remove
                  </button>
                </td>
                {orderedVehicles.map((v) => {
                  const cell = v.rates.find((r) => r.serialNo === band.serialNo);
                  return (
                    <td key={v.vehicleTypeId} className="px-2 py-1">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className={inputClass}
                        value={cell?.ratePerMile ?? ""}
                        onChange={(e) =>
                          updateRate(v.vehicleTypeId, band.serialNo, e.target.value)
                        }
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
            <tr className="bg-gray-50 font-medium">
              <td colSpan={3} className="px-3 py-3 border-r">
                Minimum fare (£)
              </td>
              {orderedVehicles.map((v) => (
                <td key={v.vehicleTypeId} className="px-2 py-2">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className={inputClass}
                    value={v.minimumFare ?? ""}
                    onChange={(e) => updateMinFare(v.vehicleTypeId, e.target.value)}
                  />
                </td>
              ))}
            </tr>
          </tbody>
        </table>
        )}
        <p className="px-6 py-3 text-xs text-gray-500 border-t">
          Minimum fare is charged when the distance-based total is lower, regardless of trip
          length.
        </p>
      </section>

      {/* Uplift pricing */}
      <section className="mt-8 rounded-2xl border border-gray-200 bg-white shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] overflow-x-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 px-6 pt-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Uplift pricing</h2>
            <p className="text-sm text-gray-500 mt-1">
              Non-base vehicle types: % uplift or flat £ uplift on the base vehicle rates.
            </p>
          </div>
          <button
            type="button"
            onClick={handleApplyUplifts}
            className={adminSecondaryButtonClass}
          >
            Apply uplifts to mileage table
          </button>
        </div>
        <table className="min-w-full text-sm mt-4 border-collapse">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="px-4 py-2 text-left w-32" />
              {orderedVehicles
                .filter((v) => !v.isMileageBase)
                .map((v) => (
                  <th
                    key={v.vehicleTypeId}
                    className="px-3 py-2 text-center font-medium text-gray-700"
                  >
                    {vehicleTypeLabel(v)}
                  </th>
                ))}
            </tr>
          </thead>
          <tbody>
            <tr className="border-b">
              <td className="px-4 py-3 text-gray-600">Percentage (%)</td>
              {orderedVehicles
                .filter((v) => !v.isMileageBase)
                .map((v) => (
                  <td key={v.vehicleTypeId} className="px-3 py-2 text-center">
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="radio"
                        name={`uplift-${v.vehicleTypeId}`}
                        checked={v.upliftMode === "percent" || v.upliftMode == null}
                        onChange={() =>
                          updateUplift(v.vehicleTypeId, "upliftMode", "percent")
                        }
                      />
                      <input
                        type="number"
                        step="1"
                        min="0"
                        className={`${inputClass} max-w-[5rem]`}
                        value={
                          v.upliftMode === "flat" ? "" : v.upliftAmount ?? ""
                        }
                        disabled={v.upliftMode === "flat"}
                        onChange={(e) =>
                          updateUplift(v.vehicleTypeId, "upliftAmount", e.target.value)
                        }
                      />
                      <span>%</span>
                    </label>
                  </td>
                ))}
            </tr>
            <tr>
              <td className="px-4 py-3 text-gray-600">Flat fee (£)</td>
              {orderedVehicles
                .filter((v) => !v.isMileageBase)
                .map((v) => (
                  <td key={v.vehicleTypeId} className="px-3 py-2 text-center">
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="radio"
                        name={`uplift-${v.vehicleTypeId}`}
                        checked={v.upliftMode === "flat"}
                        onChange={() =>
                          updateUplift(v.vehicleTypeId, "upliftMode", "flat")
                        }
                      />
                      <span>£</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className={`${inputClass} max-w-[5rem]`}
                        value={
                          v.upliftMode === "flat" ? v.upliftAmount ?? "" : ""
                        }
                        disabled={v.upliftMode !== "flat"}
                        onChange={(e) =>
                          updateUplift(v.vehicleTypeId, "upliftAmount", e.target.value)
                        }
                      />
                    </label>
                  </td>
                ))}
            </tr>
          </tbody>
        </table>
      </section>

      {/* Calculator */}
      <section className="mt-8 rounded-2xl border border-gray-200 bg-white shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] overflow-x-auto">
        <h2 className="text-lg font-semibold px-6 pt-6 pb-2 text-gray-800">
          Mileage pricing calculator
        </h2>
        <p className="px-6 pb-4 text-sm text-gray-500">
          Preview fares from your current settings (updates as you edit).
        </p>
        <table className="min-w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="px-4 py-2 text-left font-medium text-gray-600">Distance</th>
              {orderedVehicles.map((v) => (
                <th
                  key={v.vehicleTypeId}
                  className="px-3 py-2 text-center font-medium text-gray-700"
                >
                  {vehicleTypeLabel(v)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {calculatorRows.map((row) => (
              <tr key={row.distance} className="border-b">
                <td className="px-4 py-2 text-gray-800 font-medium">{row.distance} miles</td>
                {row.fares.map((f) => (
                  <td key={f.vehicleTypeId} className="px-3 py-2 text-center">
                    {f.fare != null ? formatMoney(f.fare) : "—"}
                  </td>
                ))}
              </tr>
            ))}
            <tr className="bg-brand-50/50 dark:bg-brand-500/5">
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    step="0.1"
                    min="0.1"
                    placeholder="Custom"
                    className={`${inputClass} max-w-[6rem]`}
                    value={customMiles}
                    onChange={(e) => setCustomMiles(e.target.value)}
                  />
                  <span className="text-gray-600">miles</span>
                </div>
              </td>
              {customFares
                ? customFares.map((f) => (
                    <td key={f.vehicleTypeId} className="px-3 py-2 text-center font-medium">
                      {formatMoney(f.fare)}
                    </td>
                  ))
                : orderedVehicles.map((v) => (
                    <td key={v.vehicleTypeId} className="px-3 py-2 text-center text-gray-400">
                      —
                    </td>
                  ))}
            </tr>
          </tbody>
        </table>
      </section>

      <div className="mt-8 flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className={adminPrimaryButtonClass}
        >
          {saving ? "Saving..." : "Save all pricing"}
        </button>
      </div>
    </AdminPageShell>
  );
}
